import api from './api';

export const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

/** App-wide default page size when settings / localStorage do not specify one. */
export const DEFAULT_ITEMS_PER_PAGE = 10;

/** Map saved or legacy values onto allowed page sizes (10, 25, 50). */
export const clampItemsPerPage = (value) => {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return DEFAULT_ITEMS_PER_PAGE;
  if (ITEMS_PER_PAGE_OPTIONS.includes(n)) return n;
  if (n <= 10) return 10;
  if (n <= 25) return 25;
  return 50;
};

// Cache for settings to avoid repeated API calls
let settingsCache = null;
let settingsCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCurrentUserRole = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return String(user?.role || '').trim().toLowerCase();
  } catch (error) {
    return '';
  }
};

/**
 * Get default items per page from General Settings (from database)
 * Falls back to localStorage, then to 10 if not set
 */
export const getDefaultItemsPerPage = async () => {
  try {
    const currentUserRole = getCurrentUserRole();
    const canReadSettingsApi = currentUserRole === 'admin';

    // Check cache first
    if (settingsCache && settingsCacheTime && (Date.now() - settingsCacheTime) < CACHE_DURATION) {
      return clampItemsPerPage(settingsCache.defaultItemsPerPage || DEFAULT_ITEMS_PER_PAGE);
    }

    // Settings endpoint is admin-only. Skip API calls for non-admin users.
    if (canReadSettingsApi) {
      const response = await api.getSettings();
      if (response.success && response.data && response.data.defaultItemsPerPage) {
        settingsCache = response.data;
        settingsCacheTime = Date.now();
        return clampItemsPerPage(response.data.defaultItemsPerPage);
      }
    }
  } catch (error) {
    // Suppress noisy expected auth errors; fallback to local settings below.
    const message = String(error?.message || '').toLowerCase();
    if (!message.includes('not authorized')) {
      console.error('Error fetching settings from API:', error);
    }
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem('generalSettings');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.defaultItemsPerPage) {
          return clampItemsPerPage(parsed.defaultItemsPerPage);
        }
      }
    } catch (e) {
      console.error('Error reading local settings:', e);
    }
  }
  return DEFAULT_ITEMS_PER_PAGE;
};

/**
 * Synchronous version that uses cache or localStorage
 * Use this for initial state, then update with async version
 */
export const getDefaultItemsPerPageSync = () => {
  // Check cache first
  if (settingsCache && settingsCache.defaultItemsPerPage) {
    return clampItemsPerPage(settingsCache.defaultItemsPerPage);
  }

  // Fallback to localStorage
  try {
    const stored = localStorage.getItem('generalSettings');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.defaultItemsPerPage) {
        return clampItemsPerPage(parsed.defaultItemsPerPage);
      }
    }
  } catch (error) {
    console.error('Error reading local settings:', error);
  }
  return DEFAULT_ITEMS_PER_PAGE;
};

/**
 * Clear settings cache (useful after updating settings)
 */
export const clearSettingsCache = () => {
  settingsCache = null;
  settingsCacheTime = null;
};

/**
 * Get all general settings
 */
export const getGeneralSettings = () => {
  try {
    const stored = localStorage.getItem('generalSettings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading general settings:', error);
  }
  return {
    appName: 'Asset Management',
    companyName: '',
    defaultItemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    enableEmailNotifications: true,
    enableIssueEmails: true,
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
  };
};

